import React, { useEffect, useState, useRef } from 'react';
import { CheckIcon } from 'lucide-react';
import { Button } from '../components/ui/Button';
import { ServiceCard } from '../components/servis/ServiceCard';
import { ServisRequestForm } from '../components/servis/ServisRequestForm';
import { PartnerForm } from '../components/servis/PartnerForm';
import { servisers } from '../data/servisers';
export function ServicePage() {
  const requestFormRef = useRef<HTMLDivElement>(null);
  const [sectionsVisible, setSectionsVisible] = useState<Record<string, boolean>>({});
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  useEffect(() => {
    const observer = new IntersectionObserver(entries => {
      entries.forEach(entry => {
        if (entry.isIntersecting && entry.target.id) {
          setSectionsVisible(prev => ({
            ...prev,
            [entry.target.id]: true
          }));
        }
      });
    }, {
      threshold: 0.15
    });
    document.querySelectorAll('[data-animate]').forEach(el => observer.observe(el));
    return () => observer.disconnect();
  }, []);
  const scrollToRequestForm = () => {
    requestFormRef.current?.scrollIntoView({
      behavior: 'smooth',
      block: 'start'
    });
  };
  const benefits = ['Tehničku obuku', 'Pristup rezervnim delovima', 'Podršku proizvođača', 'Uvrštavanje u zvaničnu servisnu mrežu', 'Regionalnu ekskluzivu (po dogovoru)'];
  const requirements = ['Registrovana radnja ili firma', 'Iskustvo u radu sa elektro ili industrijskom opremom', 'Terensko vozilo', 'Osnovni alat i dijagnostička oprema', 'Spremnost za terenski rad'];
  return <main className="min-h-screen bg-dark-bg" data-id="element-820">
      {/* Hero */}
      <section className="py-10 md:py-14 px-4 sm:px-6 lg:px-8 border-b border-dark-border" data-id="element-821">
        <div className="max-w-4xl mx-auto text-center" data-id="element-822">
          <h1 className="text-4xl md:text-5xl font-black text-white mb-4" data-id="element-823">
            Ovlašćeni servis i{' '}
            <span className="text-brand-yellow" data-id="element-824">tehnička podrška</span>
          </h1>
          <p className="text-lg text-gray-300" data-id="element-825">
            Stručan servis, dostupni rezervni delovi i podrška širom Srbije.
          </p>
        </div>
      </section>

      {/* Servisna mreža */}
      <section ref={requestFormRef} className="py-10 md:py-14 px-4 sm:px-6 lg:px-8 border-b border-dark-border" data-id="element-826">
        <div className="max-w-6xl mx-auto" data-id="element-827">
          <h2 id="mreza" data-animate className={`text-3xl md:text-4xl font-black text-white mb-8 text-center transition-all duration-600 ${sectionsVisible.mreza ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`} data-id="element-828">
            Servisna <span className="text-brand-yellow" data-id="element-829">mreža</span>
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5" data-id="element-830">
            {servisers.map((serviser, index) => <ServiceCard key={serviser.id} serviser={serviser} index={index} data-id="element-831" />)}
          </div>
        </div>
      </section>

      {/* Postani serviser */}
      <section className="py-10 md:py-14 px-4 sm:px-6 lg:px-8 border-b border-dark-border" data-id="element-832">
        <div className="max-w-6xl mx-auto" data-id="element-833">
          <h2 id="partner" data-animate className={`text-3xl md:text-4xl font-black text-white mb-4 text-center transition-all duration-600 ${sectionsVisible.partner ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`} data-id="element-834">
            Postanite D-TECH{' '}
            <span className="text-brand-yellow" data-id="element-835">ovlašćeni serviser</span>
          </h2>
          <p className="text-gray-300 text-center mb-10 max-w-3xl mx-auto" data-id="element-836">
            Širimo servisnu mrežu i tražimo tehnički osposobljene partnere za
            rad sa D-TECH SCB 300 sistemima. Obezbeđujemo tehničku obuku,
            podršku i dostupnost rezervnih delova.
          </p>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8" data-id="element-837">
            {/* Left column */}
            <div data-id="element-838">
              <h3 className="text-xl font-bold text-white mb-4" data-id="element-839">
                Šta <span className="text-brand-yellow" data-id="element-840">nudimo:</span>
              </h3>
              <ul className="space-y-3" data-id="element-841">
                {benefits.map((benefit, index) => <li key={index} className="flex items-start gap-3 text-gray-300" data-id="element-842">
                    <CheckIcon className="w-5 h-5 text-brand-yellow flex-shrink-0 mt-0.5" data-id="element-843" />
                    <span data-id="element-844">{benefit}</span>
                  </li>)}
              </ul>
            </div>

            {/* Right column */}
            <div className="bg-dark-surface border border-dark-border rounded-xl p-6" data-id="element-845">
              <h3 className="text-xl font-bold text-white mb-5" data-id="element-846">
                Prijava za servisnu{' '}
                <span className="text-brand-yellow" data-id="element-847">saradnju</span>
              </h3>
              <PartnerForm data-id="element-848" />
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-10 md:py-14 px-4 sm:px-6 lg:px-8 bg-dark-surface border-t border-dark-border" data-id="element-849">
        <div className="max-w-3xl mx-auto text-center" data-id="element-850">
          <h2 className="text-3xl md:text-4xl font-black text-white mb-4" data-id="element-851">
            Potrebna vam je{' '}
            <span className="text-brand-yellow" data-id="element-852">intervencija?</span>
          </h2>
          <p className="text-lg text-gray-300 mb-8" data-id="element-853">
            Kontaktirajte nadležnog servisera ili pošaljite zahtev online.
          </p>
          <Button variant="primary" size="lg" onClick={scrollToRequestForm} data-id="element-854">
            Zatražite servis
          </Button>
        </div>
      </section>
    </main>;
}